"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { AstroMascot } from "./AstroMascot";
import { 
  ArrowLeft, 
  ArrowRight,
  Check,
  AlertTriangle,
  Edit3,
  User,
  GraduationCap,
  Briefcase,
  Sparkles,
  Rocket,
  Flag,
  HelpCircle,
  CheckCircle2,
  Info
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip";
const openTurfLogo = '/openturf-logo.svg';

interface Field {
  id: string;
  label: string;
  value: string;
  confidence: 'high' | 'medium' | 'low';
  type: 'text' | 'email' | 'phone' | 'date';
  section: 'identity' | 'education' | 'experience';
  isManuallyEntered?: boolean;
  originalValue?: string;
}

interface Section {
  id: 'identity' | 'education' | 'experience';
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  description: string;
}

interface AstroAIReviewProps {
  onReviewComplete: () => void;
  onShowTerms?: () => void;
  onBack: () => void;
}

export function AstroAIReview({ 
  onReviewComplete, 
  onShowTerms, 
  onBack 
}: AstroAIReviewProps) {
  // Mock extracted data with different confidence levels
  const [fields] = React.useState<Field[]>([
    // Identity Section
    { id: 'fullName', label: 'Full Name', value: 'John Michael Smith', confidence: 'high', type: 'text', section: 'identity' },
    { id: 'email', label: 'Email Address', value: 'j.smith@email.com', confidence: 'medium', type: 'email', section: 'identity' },
    { id: 'phone', label: 'Phone Number', value: '+1 (555) 123-4567', confidence: 'high', type: 'phone', section: 'identity' },
    { id: 'dateOfBirth', label: 'Date of Birth', value: 'March 15, 1990', confidence: 'low', type: 'date', section: 'identity' },
    
    // Education Section
    { id: 'university', label: 'University', value: 'Stanford University', confidence: 'high', type: 'text', section: 'education' },
    { id: 'degree', label: 'Degree', value: 'Master of Computer Science', confidence: 'medium', type: 'text', section: 'education' },
    { id: 'graduationYear', label: 'Graduation Year', value: '2015', confidence: 'high', type: 'date', section: 'education' },
    
    // Experience Section
    { id: 'currentCompany', label: 'Current Company', value: 'Tech Solutions Inc.', confidence: 'high', type: 'text', section: 'experience' },
    { id: 'currentRole', label: 'Current Role', value: 'Senior Software Engineer', confidence: 'medium', type: 'text', section: 'experience' },
    { id: 'experience', label: 'Years of Experience', value: '8 years', confidence: 'low', type: 'text', section: 'experience' }
  ]);

  const sections: Section[] = [
    {
      id: 'identity',
      title: 'Personal Identity',
      icon: User,
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      description: 'Let\'s verify your personal details'
    },
    {
      id: 'education',
      title: 'Education Background',
      icon: GraduationCap,
      color: 'bg-purple-100 text-purple-700 border-purple-200',
      description: 'Time to check your educational qualifications'
    },
    {
      id: 'experience',
      title: 'Work Experience',
      icon: Briefcase,
      color: 'bg-green-100 text-green-700 border-green-200',
      description: 'Let\'s review your professional journey'
    }
  ];

  const [currentSectionIndex, setCurrentSectionIndex] = React.useState(0);
  const [currentFieldIndex, setCurrentFieldIndex] = React.useState(0);
  const [editedValues, setEditedValues] = React.useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = React.useState(false);
  const [completedFields, setCompletedFields] = React.useState<Set<string>>(new Set());
  const [manuallyEnteredFields, setManuallyEnteredFields] = React.useState<Set<string>>(new Set());
  const [showManualEntrySuccess, setShowManualEntrySuccess] = React.useState(false);

  const currentSection = sections[currentSectionIndex];
  const currentSectionFields = fields.filter(field => field.section === currentSection.id);
  const currentField = currentSectionFields[currentFieldIndex];
  const totalSteps = sections.length;
  const currentStep = currentSectionIndex + 1;

  const getFieldValue = (fieldId: string) => {
    return editedValues[fieldId] || fields.find(f => f.id === fieldId)?.value || '';
  };

  const getConfidenceMessage = (confidence: 'high' | 'medium' | 'low', isManualEntry?: boolean) => {
    if (isManualEntry) {
      return "Thanks! I'll mark this for HR to double-check, just in case.";
    }
    
    switch (confidence) {
      case 'high':
        return "Here's what I found. Looks good?";
      case 'medium':
        return "I'm pretty confident about this one. Does it look right?";
      case 'low':
        return "Hmm, this one's a bit fuzzy. Could you help me out here?";
    }
  };

  const getAstroExpression = (confidence: 'high' | 'medium' | 'low', isManualEntry?: boolean) => {
    if (isManualEntry) {
      return 'happy';
    }
    
    switch (confidence) {
      case 'high':
        return 'happy';
      case 'medium':
        return 'curious';
      case 'low':
        return 'confused';
      default:
        return 'happy';
    }
  };

  const handleFieldEdit = (value: string) => {
    setEditedValues(prev => ({
      ...prev,
      [currentField.id]: value
    }));
  };

  const handleManualEntry = () => {
    setIsEditing(true);
    // Mark as manually entered if confidence is low
    if (currentField.confidence === 'low') {
      setManuallyEnteredFields(prev => new Set([...prev, currentField.id]));
    }
  };

  const handleSaveManualEntry = () => {
    setIsEditing(false);
    if (currentField.confidence === 'low') {
      setShowManualEntrySuccess(true);
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowManualEntrySuccess(false);
      }, 3000);
    }
  };

  const handleConfirmField = () => {
    setCompletedFields(prev => new Set([...prev, currentField.id]));
    setIsEditing(false);
    
    // Move to next field or section
    if (currentFieldIndex < currentSectionFields.length - 1) {
      setCurrentFieldIndex(prev => prev + 1);
    } else if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
      setCurrentFieldIndex(0);
    } else {
      // All fields completed
      handleComplete();
    }
  };

  const handleComplete = () => {
    setTimeout(() => {
      onReviewComplete();
    }, 1500);
  };

  const isLastField = currentSectionIndex === sections.length - 1 && 
                     currentFieldIndex === currentSectionFields.length - 1;

  const overallProgress = ((completedFields.size) / fields.length) * 100;
  
  const isManuallyEntered = manuallyEnteredFields.has(currentField.id);
  const shouldShowFallback = currentField.confidence === 'low' && !completedFields.has(currentField.id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex flex-col relative overflow-hidden">
      {/* Soft background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-32 left-16 w-32 h-32 bg-indigo-100/30 rounded-full blur-3xl"
          animate={{ 
            y: [0, -20, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-32 right-16 w-40 h-40 bg-purple-100/30 rounded-full blur-3xl"
          animate={{ 
            y: [0, 20, 0],
            scale: [1.1, 1, 1.1]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />
      </div>

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/70 backdrop-blur-sm border-b border-gray-200/50"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-gray-700 hover:text-gray-900 hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-gray-300" />
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-gray-700">
              <Rocket className="w-5 h-5 text-indigo-600" />
              <span>New Workflow - Step 3</span>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-2xl mx-auto relative z-10 w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${currentSection.id}-${currentField.id}`}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="space-y-8"
            >
              {/* Progress Section */}
              <div className="text-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${currentSection.color}`}>
                      <currentSection.icon className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <h2 className="text-gray-900 text-lg">{currentSection.title}</h2>
                      <p className="text-gray-600 text-sm">{currentSection.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-center space-x-4 mb-4">
                    <Badge variant="secondary" className="text-xs">
                      Step {currentStep} of {totalSteps}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {currentFieldIndex + 1} of {currentSectionFields.length} fields
                    </Badge>
                  </div>

                  <Progress value={overallProgress} className="h-2 max-w-md mx-auto" />
                  <p className="text-xs text-gray-500 mt-2">
                    {completedFields.size} of {fields.length} fields verified
                  </p>
                </motion.div>
              </div>

              {/* Main Field Review */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="space-y-6"
              >
                {/* Field Label with Manual Entry Tag */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Label className="text-gray-900">{currentField.label}</Label>
                    {isManuallyEntered && (
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs">
                          <Flag className="w-3 h-3 mr-1" />
                          Flagged for Review
                        </Badge>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-gray-400 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>This field was manually entered and will be double-checked by HR</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </div>

                  {/* Astro Guide - Only show for flagged sections or manual entry success */}
                  {(shouldShowFallback || showManualEntrySuccess || isManuallyEntered) && (
                    <div className="flex items-start space-x-6">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        <AstroMascot 
                          expression={getAstroExpression(currentField.confidence, showManualEntrySuccess || isManuallyEntered)} 
                          size="lg" 
                        />
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                        className="flex-1"
                      >
                        <div className={`backdrop-blur-sm rounded-2xl p-6 shadow-lg border relative ${
                          showManualEntrySuccess || isManuallyEntered 
                            ? 'bg-green-50/80 border-green-200' 
                            : shouldShowFallback 
                              ? 'bg-amber-50/80 border-amber-200'
                              : 'bg-white/80 border-white/20'
                        }`}>
                          {/* Speech bubble pointer */}
                          <div className={`absolute left-0 top-6 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[12px] -translate-x-3 ${
                            showManualEntrySuccess || isManuallyEntered 
                              ? 'border-r-green-50/80' 
                              : shouldShowFallback 
                                ? 'border-r-amber-50/80'
                                : 'border-r-white/80'
                          }`} />
                          
                          <div className="space-y-4">
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-900">
                                {getConfidenceMessage(currentField.confidence, showManualEntrySuccess || isManuallyEntered)}
                              </span>
                              {shouldShowFallback && (
                                <HelpCircle className="w-4 h-4 text-amber-600" />
                              )}
                              {(showManualEntrySuccess || isManuallyEntered) && (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  )}

                  {/* Field Preview/Edit */}
                  <div className="space-y-3">
                    {!isEditing ? (
                      <div className={`flex items-center justify-between p-4 rounded-xl border ${
                        shouldShowFallback 
                          ? 'bg-amber-50 border-amber-200' 
                          : isManuallyEntered
                            ? 'bg-green-50 border-green-200'
                            : 'bg-gray-50 border-gray-200'
                      }`}>
                        <span className="text-gray-900">{getFieldValue(currentField.id)}</span>
                        <div className="flex items-center space-x-2">
                          {shouldShowFallback && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleManualEntry}
                              className="text-amber-700 border-amber-300 hover:bg-amber-100"
                            >
                              <Edit3 className="w-4 h-4 mr-2" />
                              Enter Manually
                            </Button>
                          )}
                          {!shouldShowFallback && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setIsEditing(true)}
                              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                            >
                              <Edit3 className="w-4 h-4 mr-2" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Input
                          type={currentField.type}
                          value={getFieldValue(currentField.id)}
                          onChange={(e) => handleFieldEdit(e.target.value)}
                          className={`bg-white border-2 focus:ring-2 ${
                            shouldShowFallback 
                              ? 'border-amber-300 focus:border-amber-400 focus:ring-amber-400/50' 
                              : 'border-indigo-200 focus:border-indigo-400 focus:ring-indigo-400/50'
                          }`}
                          autoFocus
                          placeholder={`Enter ${currentField.label.toLowerCase()}`}
                        />
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            onClick={() => setIsEditing(false)}
                            variant="outline"
                            className="text-gray-600"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={shouldShowFallback ? handleSaveManualEntry : () => setIsEditing(false)}
                            className={shouldShowFallback 
                              ? "bg-amber-600 hover:bg-amber-700 text-white" 
                              : "bg-indigo-600 hover:bg-indigo-700 text-white"
                            }
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Confidence Indicator - Hide for manual entries */}
                  {!isManuallyEntered && !showManualEntrySuccess && (
                    <div className="flex items-center space-x-2 text-xs">
                      <span className="text-gray-500">AI Confidence:</span>
                      <div className="flex space-x-1">
                        {[1, 2, 3].map((level) => (
                          <div
                            key={level}
                            className={`w-2 h-2 rounded-full ${
                              level <= (currentField.confidence === 'high' ? 3 : currentField.confidence === 'medium' ? 2 : 1)
                                ? currentField.confidence === 'high' 
                                  ? 'bg-green-500' 
                                  : currentField.confidence === 'medium' 
                                    ? 'bg-yellow-500' 
                                    : 'bg-red-500'
                                : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <span className={`capitalize ${
                        currentField.confidence === 'high' 
                          ? 'text-green-600' 
                          : currentField.confidence === 'medium' 
                            ? 'text-yellow-600' 
                            : 'text-red-600'
                      }`}>
                        {currentField.confidence}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-center"
                >
                  <Button
                    onClick={handleConfirmField}
                    disabled={isEditing}
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-8 py-3"
                  >
                    {completedFields.has(currentField.id) ? (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Verified
                      </>
                    ) : (
                      <>
                        {isLastField ? 'Complete Review' : 'Confirm & Continue'}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </motion.div>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/70 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-indigo-600 hover:text-indigo-700 hover:underline transition-colors"
            >
              Terms &amp; Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}